import React, { useState, Fragment } from 'react';
import { useCountdown } from '../hooks/useCountdown';
import { Lock, Zap, ShieldCheck, ArrowRight, Clock, CreditCard } from 'lucide-react';
type PlanKey = 'base' | 'pro';
const plans: Record<PlanKey, {
  emoji: string;
  name: string;
  includes: string;
  oldPrice: string;
  price: string;
  save?: string;
  highlight: boolean;
  ctaLabel: string;
  url: string;
}> = {
  base: {
    emoji: '🤟',
    name: 'SISTEMA LSA FÁCIL',
    includes: '7 tomos + 6 bonos estratégicos',
    oldPrice: '$49.000',
    price: '$19.990',
    highlight: false,
    ctaLabel: 'SÍ, QUIERO EL SISTEMA LSA FÁCIL',
    url: 'https://checkout.tusbeneficios.shop/cart/51269870780649:1'
  },
  pro: {
    emoji: '🚀',
    name: 'PLAN PROFESIONAL',
    includes: '7 tomos + 6 bonos + 3 módulos profesionales',
    oldPrice: '$69.000',
    price: '$27.990',
    save: 'AHORRÁS $41.000',
    highlight: true,
    ctaLabel: 'SÍ, QUIERO EL PLAN PROFESIONAL',
    url: 'https://checkout.tusbeneficios.shop/cart/51269870846185:1'
  }
};
export function Pricing() {
  const [selected, setSelected] = useState<PlanKey>('pro');
  const {
    hours,
    minutes,
    seconds
  } = useCountdown(24, 0, 0);
  const order: PlanKey[] = ['base', 'pro'];
  return <section id="pricing" className="bg-lsa-deep text-white py-12 px-5 border-t border-white/10" data-id="element-178">
      <div className="max-w-md mx-auto" data-id="element-179">
        {/* TIMER */}
        <div className="mb-8 flex flex-col items-center" data-id="element-180">
          <p className="text-lsa-cyan font-bold text-sm tracking-widest mb-3 uppercase flex items-center gap-2" data-id="element-181">
            <Clock size={16} data-id="element-182" /> Esta oferta termina en
          </p>
          <div className="flex items-center gap-2 sm:gap-3" data-id="element-183">
            {[{
            v: hours,
            l: 'HORAS',
            accent: false
          }, {
            v: minutes,
            l: 'MIN',
            accent: false
          }, {
            v: seconds,
            l: 'SEG',
            accent: true
          }].map((box, i) => <Fragment key={box.l} data-id="element-184">
                {i > 0 && <div className="text-xl font-bold text-lsa-cyan/50 -mt-5" data-id="element-185">
                    :
                  </div>}
                <div className="flex flex-col items-center" data-id="element-186">
                  <div className={`bg-[#0E1730] border border-white/10 rounded-xl w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center text-[28px] sm:text-[34px] font-bold shadow-inner ${box.accent ? 'text-lsa-cyan' : ''}`} data-id="element-187">
                    {box.v}
                  </div>
                  <span className="text-[10px] text-white/50 mt-1.5 font-semibold tracking-wider" data-id="element-188">
                    {box.l}
                  </span>
                </div>
              </Fragment>)}
          </div>
        </div>

        <div className="text-center mb-6" data-id="element-189">
          <h2 className="text-[26px] sm:text-[30px] font-extrabold mb-1.5 tracking-tight" data-id="element-190">
            ELEGÍ TU <span className="text-lsa-cyan" data-id="element-191">SISTEMA LSA FÁCIL</span>
          </h2>
          <p className="text-white/60 text-[14px] font-medium" data-id="element-192">
            Ambas opciones incluyen los 6 bonos completos
          </p>
        </div>

        {/* PLAN SELECTOR */}
        <div className="flex flex-col gap-3 mb-6" data-id="element-193">
          {order.map(key => {
          const plan = plans[key];
          const isSelected = selected === key;
          return <button key={key} type="button" onClick={() => setSelected(key)} className={`text-left relative cursor-pointer rounded-2xl p-4 transition-all duration-200 border-2 ${isSelected ? 'border-lsa-cyan bg-[#0E1730] shadow-[0_0_22px_rgba(25,199,232,0.16)]' : 'border-white/10 bg-[#0E1730]/50 hover:bg-[#0E1730]'}`} data-id="element-194">
                <div className="flex items-start gap-3" data-id="element-195">
                  <div className="mt-0.5 shrink-0" data-id="element-196">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-lsa-cyan' : 'border-white/30'}`} data-id="element-197">
                      {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-lsa-cyan" data-id="element-198" />}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-199">
                    <div className="flex items-start justify-between gap-3" data-id="element-200">
                      <h3 className={`font-bold text-[16px] flex items-center gap-1.5 ${plan.highlight ? 'text-lsa-cyan' : ''}`} data-id="element-201">
                        <span data-id="element-202">{plan.emoji}</span> {plan.name}
                      </h3>
                      <div className="flex flex-col items-end shrink-0 leading-none" data-id="element-203">
                        <span className="text-white/45 line-through text-[12px]" data-id="element-204">
                          {plan.oldPrice}
                        </span>
                        <span className={`text-[22px] font-bold leading-tight ${plan.highlight ? 'text-lsa-cyan' : ''}`} data-id="element-205">
                          {plan.price}
                        </span>
                      </div>
                    </div>

                    {(plan.highlight || plan.save) && <div className="flex flex-wrap items-center gap-2 mt-1.5" data-id="element-206">
                        {plan.highlight && <span className="bg-lsa-cyan text-lsa-deep text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider" data-id="element-207">
                            ⭐ MÁS ELEGIDO
                          </span>}
                        {plan.save && <span className="bg-green-500/20 text-green-400 text-[11px] font-bold px-2 py-0.5 rounded" data-id="element-208">
                            {plan.save}
                          </span>}
                      </div>}

                    <p className="text-white/60 text-[13px] mt-1.5" data-id="element-209">
                      {plan.includes}
                    </p>
                  </div>
                </div>
              </button>;
        })}
        </div>

        {/* CTA BUTTON */}
        <button onClick={() => {
        window.location.href = plans[selected].url;
      }} className="w-full bg-lsa-cyan hover:brightness-110 text-lsa-deep h-[56px] rounded-xl font-bold text-[16px] sm:text-[17px] transition-all shadow-lg shadow-lsa-cyan/30 flex items-center justify-center gap-2 mb-3" data-id="element-210">
          {plans[selected].ctaLabel} <ArrowRight size={18} data-id="element-211" />
        </button>

        <p className="text-center text-white/50 text-[13px] italic mb-6" data-id="element-212">
          {selected === 'pro' ? 'La opción más elegida por profesores que quieren dar clases técnicas especializadas' : 'El sistema completo para tener tus clases organizadas desde el día uno'}
        </p>

        {/* TRUST BAR */}
        <div className="flex flex-wrap justify-center items-center gap-x-5 gap-y-2 text-[13px] text-lsa-cyan/80 font-medium mb-3" data-id="element-213">
          <div className="flex items-center gap-1.5" data-id="element-214">
            <Lock size={14} data-id="element-215" /> Pago seguro
          </div>
          <div className="flex items-center gap-1.5" data-id="element-216">
            <Zap size={14} data-id="element-217" /> Acceso inmediato
          </div>
          <div className="flex items-center gap-1.5" data-id="element-218">
            <ShieldCheck size={14} data-id="element-219" /> Garantía 7 días
          </div>
        </div>

        <div className="flex justify-center items-center gap-1.5 text-[13px] text-white/50 font-medium" data-id="element-220">
          <CreditCard size={14} data-id="element-221" /> Astro Pay
        </div>
      </div>
    </section>;
}